import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Star,
  MessageSquareHeart,
  Sparkles,
  MapPin,
  Calendar,
  Users,
  Award,
  Heart,
  Compass,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Plus,
  X,
  Send,
  Loader2,
  ThumbsUp,
  Filter,
  Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import tripsApi from '../api/tripsApi';
import Loader from './Loader';

export default function TripFeedbackView() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data from API
  const [feedbackData, setFeedbackData] = useState({
    given: [],
    received: [],
    pendingReviewTrips: [],
  });

  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'testimonials'
  const [testimonialSubTab, setTestimonialSubTab] = useState('given'); // 'given' | 'received'

  // Review Modal State
  const [selectedTripForReview, setSelectedTripForReview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Form State
  const [reviewForm, setReviewForm] = useState({
    overallRating: 5,
    itineraryRating: 5,
    stayRating: 5,
    companionRating: 5,
    comment: '',
    highlights: [],
    newHighlightInput: '',
    companionShoutouts: [],
    newShoutoutName: '',
    newShoutoutMsg: '',
    willTravelAgain: true,
  });

  // Load all feedback and pending review trips
  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tripsApi.getMyFeedbacks();
      setFeedbackData({
        given: data?.given || [],
        received: data?.received || [],
        pendingReviewTrips: data?.pendingReviewTrips || [],
      });
      // If no pending trips, switch tab to testimonials
      if ((data?.pendingReviewTrips || []).length === 0 && (data?.given || []).length > 0) {
        setActiveTab('testimonials');
      }
    } catch (err) {
      console.error('Failed to load trip feedbacks:', err);
      setError('Unable to load trip reviews. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const handleOpenReviewModal = (trip) => {
    setSelectedTripForReview(trip);
    setReviewForm({
      overallRating: 5,
      itineraryRating: 5,
      stayRating: 5,
      companionRating: 5,
      comment: '',
      highlights: [],
      newHighlightInput: '',
      companionShoutouts: [],
      newShoutoutName: '',
      newShoutoutMsg: '',
      willTravelAgain: true,
    });
  };

  const handleAddHighlight = () => {
    const trimmed = reviewForm.newHighlightInput.trim();
    if (trimmed && !reviewForm.highlights.includes(trimmed)) {
      setReviewForm((prev) => ({
        ...prev,
        highlights: [...prev.highlights, trimmed],
        newHighlightInput: '',
      }));
    }
  };

  const handleRemoveHighlight = (tagToRemove) => {
    setReviewForm((prev) => ({
      ...prev,
      highlights: prev.highlights.filter((h) => h !== tagToRemove),
    }));
  };

  const handleAddShoutout = () => {
    const name = reviewForm.newShoutoutName.trim();
    const msg = reviewForm.newShoutoutMsg.trim();
    if (name && msg) {
      setReviewForm((prev) => ({
        ...prev,
        companionShoutouts: [
          ...prev.companionShoutouts,
          { travelerName: name, message: msg },
        ],
        newShoutoutName: '',
        newShoutoutMsg: '',
      }));
    }
  };

  const handleRemoveShoutout = (idxToRemove) => {
    setReviewForm((prev) => ({
      ...prev,
      companionShoutouts: prev.companionShoutouts.filter((_, idx) => idx !== idxToRemove),
    }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!selectedTripForReview) return;
    if (!reviewForm.comment.trim()) {
      alert('Please share a few words about your expedition experience.');
      return;
    }

    try {
      setIsSubmitting(true);
      await tripsApi.submitTripFeedback(selectedTripForReview.id, {
        overallRating: Number(reviewForm.overallRating),
        itineraryRating: Number(reviewForm.itineraryRating),
        stayRating: Number(reviewForm.stayRating),
        companionRating: Number(reviewForm.companionRating),
        comment: reviewForm.comment.trim(),
        highlights: reviewForm.highlights,
        companionShoutouts: reviewForm.companionShoutouts,
        willTravelAgain: Boolean(reviewForm.willTravelAgain),
      });

      setSubmitSuccess(`Your review for ${selectedTripForReview.title} has been submitted!`);
      setSelectedTripForReview(null);
      await loadFeedbacks();
    } catch (err) {
      console.error('Submit review error:', err);
      alert(`Failed to submit review: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <Loader text="Loading Expedition Reviews & Companion Feedback..." />;
  }

  const { given, received, pendingReviewTrips } = feedbackData;

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => {
          const filled = star <= rating;
          return (
            <button
              key={star}
              type="button"
              disabled={!interactive}
              onClick={() => interactive && onSelect && onSelect(star)}
              className={`${
                interactive ? 'cursor-pointer hover:scale-110 transition-transform' : 'cursor-default'
              }`}
            >
              <Star
                className={`w-4 h-4 ${
                  filled ? 'fill-amber-400 text-amber-400' : 'fill-gray-100 text-gray-300'
                }`}
              />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto space-y-8 bg-slate-50 min-h-screen">
      {/* Success Notification Banner */}
      {submitSuccess && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between gap-3 text-emerald-800 font-semibold animate-fade-in shadow-xs">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span>{submitSuccess}</span>
          </div>
          <button
            onClick={() => setSubmitSuccess('')}
            className="text-emerald-600 hover:text-emerald-900 text-xs font-bold underline cursor-pointer"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="text-xs font-bold text-[#2A9D8F] uppercase tracking-wider flex items-center gap-1.5">
              <MessageSquareHeart className="w-4 h-4" />
              <span>Community Reviews &amp; Post-Trip Synergy</span>
            </span>
            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight mt-1">
              Participant Expedition Feedback
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Share your expedition feedback, rate stays and companions, give shoutouts, and celebrate completed journeys.
            </p>
          </div>

          {/* Quick Stats Badges */}
          <div className="flex items-center gap-2">
            <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-2xl text-center">
              <span className="text-[10px] uppercase font-bold text-amber-600 block">Awaiting Review</span>
              <span className="text-lg font-black text-amber-900">{pendingReviewTrips.length}</span>
            </div>
            <div className="px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-2xl text-center">
              <span className="text-[10px] uppercase font-bold text-emerald-600 block">Reviews Given</span>
              <span className="text-lg font-black text-emerald-900">{given.length}</span>
            </div>
            <div className="px-4 py-2 bg-teal-50 border border-teal-200 rounded-2xl text-center">
              <span className="text-[10px] uppercase font-bold text-teal-600 block">Wall of Love</span>
              <span className="text-lg font-black text-teal-900">{received.length}</span>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'pending'
                ? 'bg-[#2A9D8F] text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>Awaiting Review ({pendingReviewTrips.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('testimonials')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
              activeTab === 'testimonials'
                ? 'bg-[#2A9D8F] text-white shadow-xs'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Star className="w-3.5 h-3.5" />
            <span>Expedition Testimonials ({given.length + received.length})</span>
          </button>
        </div>
      </div>

      {/* ── TAB 1: Awaiting Review ── */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-black text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />
              <span>Live &amp; Completed Expeditions Awaiting Your Review</span>
            </h2>
            <span className="text-xs text-gray-500">
              Only participants and trip organizers can review
            </span>
          </div>

          {pendingReviewTrips.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center space-y-3 border border-gray-200 shadow-sm">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <h3 className="text-base font-bold text-gray-800">You're all caught up!</h3>
              <p className="text-xs text-gray-500 max-w-md mx-auto">
                You have reviewed all your completed expeditions. When you join or complete a new shared expedition, it will appear here.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="mt-2 px-5 py-2.5 bg-[#2A9D8F] text-white text-xs font-bold rounded-xl shadow hover:bg-[#238177] cursor-pointer"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pendingReviewTrips.map((trip) => (
                <div
                  key={trip.id}
                  className="bg-white rounded-3xl overflow-hidden border border-gray-200 hover:border-teal-300 shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                >
                  <div>
                    {/* Card Cover */}
                    <div className="h-40 relative bg-gradient-to-tr from-slate-800 to-teal-900 overflow-hidden">
                      {trip.coverImageUrl ? (
                        <img
                          src={trip.coverImageUrl}
                          alt={trip.title}
                          className="w-full h-full object-cover opacity-80"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/40">
                          <Compass className="w-12 h-12" />
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-teal-400" />
                          <span>{trip.destination}</span>
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="px-2.5 py-1 rounded-full bg-amber-500 text-white text-[10px] font-bold shadow-xs flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          <span>Pending Review</span>
                        </span>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="p-5 space-y-3">
                      <h3 className="font-bold text-gray-900 text-base leading-snug line-clamp-1">
                        {trip.title}
                      </h3>

                      <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-600 pt-1 border-t border-gray-100">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-400" />
                          <span>{trip.startDate || 'TBD'} ({trip.totalDays || 3}d)</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-gray-400" />
                          <span>{trip.participantsCount} Explorer(s)</span>
                        </div>
                      </div>

                      {trip.organizer && (
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                          <img
                            src={trip.organizer.avatarUrl || `https://api.dicebear.com/7.x/bottts/svg?seed=${trip.organizer.id}`}
                            alt={trip.organizer.name}
                            className="w-5 h-5 rounded-full object-cover"
                          />
                          <span className="text-[11px] text-gray-500 truncate">
                            Organized by <strong className="text-gray-700">{trip.organizer.name}</strong>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Action */}
                  <div className="p-5 pt-0">
                    <button
                      onClick={() => handleOpenReviewModal(trip)}
                      className="w-full py-2.5 bg-[#2A9D8F] hover:bg-[#238177] text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <MessageSquareHeart className="w-4 h-4" />
                      <span>Write Expedition Review</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB 2: Testimonials & Wall of Love ── */}
      {activeTab === 'testimonials' && (
        <div className="space-y-6">
          {/* Sub-tab navigation */}
          <div className="flex items-center justify-between border-b border-gray-200 pb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTestimonialSubTab('given')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  testimonialSubTab === 'given'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Reviews Given by You ({given.length})
              </button>
              <button
                onClick={() => setTestimonialSubTab('received')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  testimonialSubTab === 'received'
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                Reviews Received on Your Trips ({received.length})
              </button>
            </div>
          </div>

          {/* Testimonial List */}
          {((testimonialSubTab === 'given' ? given : received).length === 0) ? (
            <div className="bg-white rounded-3xl p-12 text-center space-y-3 border border-gray-200 shadow-sm">
              <Award className="w-12 h-12 text-gray-300 mx-auto" />
              <h3 className="text-base font-bold text-gray-700">No Reviews Found</h3>
              <p className="text-xs text-gray-500 max-w-sm mx-auto">
                {testimonialSubTab === 'given'
                  ? "You haven't submitted any reviews yet. Check the 'Awaiting Review' tab!"
                  : "No participants have submitted reviews for your organized trips yet."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {(testimonialSubTab === 'given' ? given : received).map((fb) => (
                <div
                  key={fb.id}
                  className="bg-white rounded-3xl p-6 border border-gray-200 hover:border-gray-300 shadow-xs space-y-4 flex flex-col justify-between"
                >
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={fb.user?.avatarUrl || `https://api.dicebear.com/7.x/bottts/svg?seed=${fb.userId}`}
                          alt={fb.user?.name || 'Traveler'}
                          className="w-10 h-10 rounded-2xl object-cover bg-gray-100 shrink-0"
                        />
                        <div>
                          <h4 className="text-sm font-bold text-gray-900">{fb.user?.name || 'Explorer'}</h4>
                          <span className="text-[11px] text-gray-400">
                            {new Date(fb.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        {renderStars(fb.overallRating)}
                        <span className="text-[10px] font-bold text-amber-600 mt-0.5">
                          {fb.overallRating}.0 / 5.0
                        </span>
                      </div>
                    </div>

                    {/* Trip Link Badge */}
                    {fb.sharedTrip && (
                      <div
                        onClick={() => navigate(`/dashboard/trip/${fb.sharedTrip.id}`)}
                        className="p-2.5 bg-gray-50 hover:bg-teal-50/50 rounded-xl border border-gray-100 flex items-center justify-between cursor-pointer transition-colors"
                      >
                        <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 truncate">
                          <Compass className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                          <span className="truncate">{fb.sharedTrip.title}</span>
                          <span className="text-gray-400">({fb.sharedTrip.destination})</span>
                        </div>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                    )}

                    {/* Comment */}
                    <blockquote className="text-xs text-gray-700 leading-relaxed italic bg-slate-50/70 p-3.5 rounded-xl border-l-2 border-[#2A9D8F]">
                      "{fb.comment}"
                    </blockquote>

                    {/* Category Rating Badges */}
                    <div className="grid grid-cols-3 gap-2 text-[10px] text-gray-600">
                      <div className="p-2 bg-gray-50 rounded-lg text-center">
                        <span className="text-gray-400 block">Itinerary</span>
                        <span className="font-bold text-gray-800">{fb.itineraryRating || 5}★</span>
                      </div>
                      <div className="p-2 bg-gray-50 rounded-lg text-center">
                        <span className="text-gray-400 block">Stays</span>
                        <span className="font-bold text-gray-800">{fb.stayRating || 5}★</span>
                      </div>
                      <div className="p-2 bg-gray-50 rounded-lg text-center">
                        <span className="text-gray-400 block">Companions</span>
                        <span className="font-bold text-gray-800">{fb.companionRating || 5}★</span>
                      </div>
                    </div>

                    {/* Highlights Pills */}
                    {Array.isArray(fb.highlights) && fb.highlights.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        {fb.highlights.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-teal-50 text-teal-800 border border-teal-200 rounded-md text-[10px] font-semibold"
                          >
                            🌟 {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Companion Shoutouts */}
                    {Array.isArray(fb.companionShoutouts) && fb.companionShoutouts.length > 0 && (
                      <div className="p-3 bg-rose-50/50 border border-rose-100 rounded-xl space-y-1 text-xs">
                        <span className="text-[10px] font-bold text-rose-800 uppercase tracking-wider flex items-center gap-1">
                          <Heart className="w-3 h-3 text-rose-500" /> Companion Shoutouts:
                        </span>
                        {fb.companionShoutouts.map((s, idx) => (
                          <p key={idx} className="text-[11px] text-rose-900">
                            <strong>@{s.travelerName}:</strong> {s.message}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Travel Again Indicator */}
                  {fb.willTravelAgain && (
                    <div className="pt-2 border-t border-gray-100 flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                      <ThumbsUp className="w-3.5 h-3.5" />
                      <span>Would gladly travel with this companion roster again!</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Review Submission Modal ── */}
      {selectedTripForReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 space-y-6 shadow-2xl border border-gray-100">
            {/* Modal Header */}
            <div className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3">
              <div>
                <span className="text-[10px] font-black text-teal-600 uppercase tracking-wider">
                  Post-Trip Participant Review
                </span>
                <h3 className="text-xl font-black text-gray-900 mt-0.5">
                  Review {selectedTripForReview.title}
                </h3>
                <p className="text-xs text-gray-500">
                  {selectedTripForReview.destination} • {selectedTripForReview.totalDays || 3} Days
                </p>
              </div>
              <button
                onClick={() => setSelectedTripForReview(null)}
                className="p-1 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmitReview} className="space-y-5 text-xs">
              {/* Star Ratings Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Overall Expedition</label>
                  {renderStars(reviewForm.overallRating, true, (val) =>
                    setReviewForm({ ...reviewForm, overallRating: val })
                  )}
                </div>
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Itinerary &amp; Pacing</label>
                  {renderStars(reviewForm.itineraryRating, true, (val) =>
                    setReviewForm({ ...reviewForm, itineraryRating: val })
                  )}
                </div>
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Stays &amp; Comfort</label>
                  {renderStars(reviewForm.stayRating, true, (val) =>
                    setReviewForm({ ...reviewForm, stayRating: val })
                  )}
                </div>
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Companion Synergy</label>
                  {renderStars(reviewForm.companionRating, true, (val) =>
                    setReviewForm({ ...reviewForm, companionRating: val })
                  )}
                </div>
              </div>

              {/* Comment Textarea */}
              <div>
                <label className="font-bold text-gray-700 block mb-1">
                  Trip Reflection &amp; Participant Experience *
                </label>
                <textarea
                  rows={4}
                  required
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                  placeholder="How was the journey? Share your favorite memories, recommendations for future travelers, and group vibes."
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-xs leading-relaxed"
                />
              </div>

              {/* Highlights Tags */}
              <div className="space-y-2">
                <label className="font-bold text-gray-700 block">Expedition Highlights</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={reviewForm.newHighlightInput}
                    onChange={(e) => setReviewForm({ ...reviewForm, newHighlightInput: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddHighlight();
                      }
                    }}
                    placeholder="e.g. Sunset Trek, Campfire, Street Food tour"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-xs"
                  />
                  <button
                    type="button"
                    onClick={handleAddHighlight}
                    className="px-4 py-2 bg-gray-900 text-white rounded-xl font-bold hover:bg-black cursor-pointer text-xs"
                  >
                    Add
                  </button>
                </div>

                {reviewForm.highlights.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {reviewForm.highlights.map((h, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-teal-50 text-teal-800 border border-teal-200 rounded-lg text-xs font-semibold"
                      >
                        <span>{h}</span>
                        <X
                          className="w-3 h-3 cursor-pointer hover:text-red-500"
                          onClick={() => handleRemoveHighlight(h)}
                        />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Companion Shoutout */}
              <div className="space-y-2">
                <label className="font-bold text-gray-700 block">
                  Companion Shoutouts (Optional)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <input
                    type="text"
                    value={reviewForm.newShoutoutName}
                    onChange={(e) => setReviewForm({ ...reviewForm, newShoutoutName: e.target.value })}
                    placeholder="Companion Name"
                    className="px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-xs"
                  />
                  <input
                    type="text"
                    value={reviewForm.newShoutoutMsg}
                    onChange={(e) => setReviewForm({ ...reviewForm, newShoutoutMsg: e.target.value })}
                    placeholder="Shoutout note (e.g. Best driver!)"
                    className="sm:col-span-2 px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#2A9D8F] outline-none text-xs"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddShoutout}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold cursor-pointer"
                  >
                    + Add Shoutout
                  </button>
                </div>

                {reviewForm.companionShoutouts.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {reviewForm.companionShoutouts.map((s, idx) => (
                      <div
                        key={idx}
                        className="p-2 bg-rose-50 border border-rose-100 rounded-lg flex items-center justify-between text-xs text-rose-900"
                      >
                        <span>
                          <strong>@{s.travelerName}:</strong> {s.message}
                        </span>
                        <X
                          className="w-3.5 h-3.5 cursor-pointer text-rose-500 hover:text-rose-800"
                          onClick={() => handleRemoveShoutout(idx)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Will Travel Again Switch */}
              <div className="flex items-center justify-between p-3.5 bg-gray-50 rounded-2xl border border-gray-100">
                <span className="font-bold text-gray-800">
                  Would you travel with this companion group again?
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, willTravelAgain: true })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                      reviewForm.willTravelAgain
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white border text-gray-600'
                    }`}
                  >
                    Yes, definitely!
                  </button>
                  <button
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, willTravelAgain: false })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                      !reviewForm.willTravelAgain
                        ? 'bg-rose-600 text-white'
                        : 'bg-white border text-gray-600'
                    }`}
                  >
                    Maybe next time
                  </button>
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setSelectedTripForReview(null)}
                  className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#2A9D8F] hover:bg-[#238177] text-white rounded-xl text-xs font-black shadow-md cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  <span>{isSubmitting ? 'Submitting Review...' : 'Publish Expedition Review'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
